import { useCallback } from "react";

import {
    AbfLoanExpanded,
    getZcCollateral,
    getAssetData,
    useManualIncreaseLeverageTransaction
} from "@bridgesplit/abf-react";
import { IncreaseLeverageTransactionInput, DEFAULT_WEIGHT_MATRIX } from "@bridgesplit/abf-sdk";
import { Result } from "@bridgesplit/utils";
import { TrackTransactionEvent } from "app/types";
import { useTransactionSender } from "app/components/transactions";

interface UseManualWindSubmitParams {
    loanExpanded: AbfLoanExpanded | undefined;
    increasePrincipalParams: IncreaseLeverageTransactionInput | undefined;
    collateralInputAmount: number | undefined;
    onSuccess?: () => void;
}

/**
 * Submit manual increase leverage (add collateral + borrow more).
 * Builds params from increasePrincipalParams + collateralInputAmount and sends via manual increase leverage transaction.
 */
export function useManualIncreaseLeverage({
    loanExpanded,
    increasePrincipalParams,
    collateralInputAmount,
    onSuccess
}: UseManualWindSubmitParams): () => Promise<Result<unknown>> {
    const collateral = getZcCollateral(loanExpanded);
    const manualWind = useManualIncreaseLeverageTransaction(loanExpanded);
    const send = useTransactionSender();
    const collateralMetadata = getAssetData(collateral?.metadata, collateral?.assetMint);

    const submit = useCallback(async () => {
        if (!increasePrincipalParams || !collateralInputAmount || !collateralMetadata) {
            return Result.errFromMessage("Missing parameters");
        }

        const params: IncreaseLeverageTransactionInput = {
            ...increasePrincipalParams,
            depositCollateral: [
                {
                    collateralAmount: collateralInputAmount,
                    collateralAssetData: collateralMetadata,
                    weightMatrixUpdate: DEFAULT_WEIGHT_MATRIX
                }
            ],
            isLoop: true
        };

        const res = await send(manualWind, params, {
            alerter: {
                generationEstimateSeconds: 5,
                showProgress: true,
                totalSteps: 1
            },
            mixpanelEvent: {
                key: TrackTransactionEvent.FlashBorrow,
                params: params
            }
        });

        if (res.isOk()) {
            onSuccess?.();
        }
        return res;
    }, [increasePrincipalParams, collateralInputAmount, collateralMetadata, manualWind, send, onSuccess]);

    return submit;
}
